import { ReactNode, useEffect, useState } from 'react'
import { useRouter } from 'next/dist/client/router'
import clsx from 'clsx'
import Link from 'next/link'

import MenuIcon from './MenuIcon'

const navLinks = [
  { href: '/ceremony', text: 'The Ceremony' },
  { href: '/need', text: 'What You Need' },
  { href: '/tradition', text: 'Tradition' },
  { href: '/testimonials', text: 'Testimonials' },
  { href: '/bris', text: 'Why Have a Bris' },
  { href: '/interfaith', text: 'Interfaith Couples' },
  { href: '/after', text: 'After the Bris' },
] as const

const Layout = ({ children }: { children: ReactNode }): JSX.Element => {
  const router = useRouter()
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false)

  useEffect(() => {
    setIsMobileNavOpen(false)
    document.body.classList.remove('overflow-hidden')
  }, [router.pathname])

  useEffect(() => {
    if (isMobileNavOpen) {
      document.body.classList.add('overflow-hidden')
    } else {
      document.body.classList.remove('overflow-hidden')
    }

    return () => {
      document.body.classList.remove('overflow-hidden')
    }
  }, [isMobileNavOpen])

  return (
    <div className="min-h-screen flex flex-col bg-cream">
      <TopNav
        isMobileNavOpen={isMobileNavOpen}
        onToggleMobileNav={() => setIsMobileNavOpen((open) => !open)}
      />
      <MobileNav isOpen={isMobileNavOpen} />

      <main className="flex-1">
        <article className="mx-auto max-w-5xl px-6 py-14 md:py-10">
          {children}
        </article>
      </main>

      <SiteFooter />
    </div>
  )
}

const TopNav: React.FC<{
  isMobileNavOpen: boolean
  onToggleMobileNav: () => void
}> = ({ onToggleMobileNav }) => {
  const router = useRouter()

  return (
    <header className="sticky top-0 z-20 border-b border-hairline bg-cream/85 backdrop-blur">
      <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-5 md:py-4">
        <Link className="font-serif text-lg text-ink" href="/">
          Robert Karasov, <span className="italic text-petrol">M.D.</span>
        </Link>

        <nav className="md:hidden">
          <ul className="flex items-center gap-6 text-sm text-ink-muted">
            {navLinks.slice(0, 4).map(({ href, text }) => {
              const active = href === router.pathname
              return (
                <li key={href}>
                  <Link
                    className={clsx('transition hover:text-petrol', {
                      'text-petrol': active,
                    })}
                    href={href}
                  >
                    {text}
                  </Link>
                </li>
              )
            })}
            <li>
              <Link
                className="rounded-md bg-petrol px-4 py-2 text-sm font-medium text-cream transition hover:bg-petrol-dark"
                href="/contact"
              >
                Contact
              </Link>
            </li>
          </ul>
        </nav>

        <button
          aria-label="Toggle menu"
          className="hidden md:block h-6 w-6 text-ink"
          onClick={onToggleMobileNav}
          type="button"
        >
          <MenuIcon />
        </button>
      </div>
    </header>
  )
}

const MobileNav: React.FC<{ isOpen: boolean }> = ({ isOpen }) => {
  const router = useRouter()

  return (
    <div
      className={clsx(
        'hidden md:block fixed inset-x-0 top-[57px] bottom-0 z-10 bg-cream transition-opacity',
        {
          'opacity-100 visible': isOpen,
          'opacity-0 invisible': !isOpen,
        }
      )}
    >
      <nav className="px-6 py-6">
        <ul className="space-y-1">
          {navLinks.map(({ href, text }) => {
            const active = href === router.pathname
            return (
              <li key={href}>
                <Link
                  className={clsx(
                    'block border-b border-hairline py-3 font-serif text-xl',
                    active ? 'text-petrol' : 'text-ink'
                  )}
                  href={href}
                >
                  {text}
                </Link>
              </li>
            )
          })}
          <li className="pt-4">
            <Link
              className="block rounded-md bg-petrol px-4 py-3 text-center text-sm font-medium text-cream"
              href="/contact"
            >
              Contact
            </Link>
          </li>
        </ul>
      </nav>
    </div>
  )
}

const SiteFooter: React.FC = () => {
  return (
    <footer className="bg-petrol text-cream">
      <div className="mx-auto grid max-w-5xl grid-cols-3 gap-10 px-6 py-12 md:grid-cols-1">
        <div>
          <p className="font-serif text-lg">Robert Karasov, M.D.</p>
          <p className="mt-2 text-sm text-cream/70">
            Pediatrician &amp; Mohel · Twin Cities
          </p>
        </div>
        <div>
          <p className="text-xs uppercase tracking-widest text-cream/60">
            Site
          </p>
          <ul className="mt-3 space-y-2 text-sm">
            <li>
              <Link className="hover:text-gold" href="/ceremony">
                The Ceremony
              </Link>
            </li>
            <li>
              <Link className="hover:text-gold" href="/tradition">
                Tradition
              </Link>
            </li>
            <li>
              <Link className="hover:text-gold" href="/testimonials">
                Testimonials
              </Link>
            </li>
            <li>
              <Link className="hover:text-gold" href="/contact">
                Contact
              </Link>
            </li>
          </ul>
        </div>
        <div>
          <p className="text-xs uppercase tracking-widest text-cream/60">
            More
          </p>
          <ul className="mt-3 space-y-2 text-sm">
            <li>
              <Link className="hover:text-gold" href="/bris">
                Why have a bris
              </Link>
            </li>
            <li>
              <Link className="hover:text-gold" href="/interfaith">
                Interfaith couples
              </Link>
            </li>
            <li>
              <Link className="hover:text-gold" href="/after">
                After the bris
              </Link>
            </li>
          </ul>
        </div>
      </div>
      <div className="border-t border-cream/10">
        <p className="mx-auto max-w-5xl px-6 py-4 text-xs text-cream/60">
          © Robert Karasov {new Date().getFullYear()}
        </p>
      </div>
    </footer>
  )
}

export default Layout
